<template>
	<Col v-if="isBuilderUnderMaintenance" class="flex-centerize width-100 height-100">
		<h2 class="reg">Ply is under maintenance, please return later</h2>
	</Col>
	<div v-else-if="editNode" class="flow-view display-flex" :class="{ 'is-grabbing': isFlowTreeDragging }">
		<LeftSideDrawer
			:workflowId="wfID"
			:versionId="versionId"
			@closeSideBar="closeLeftSideDrawer"
			:hasEditPermissions="hasEditPermissions"
			@closeRightSideBar="() => closeSideBar({ avoidRedirect: true })"
		/>
		<Col v-if="disableFlowTree" class="flex-centerize width-100 height-100">
			<h2 class="reg">This feature version has been deleted</h2>
			<AnemicButton @click="closeLeftSideDrawer" class="back-to-editing-button px-3 mt-3" tile>Back to editing</AnemicButton>
		</Col>
		<template v-else>
			<div
				ref="flowViewRef"
				class="pan-zoom-container flex-2"
				:class="{ 'flow-max-width-on-wide-mode': isWideWidget, 'un-editable-background': !hasEditPermissions }"
			>
				<FlowTree
					v-if="hasLoadedNodesData"
					ref="flowTree"
					@run-feedback-flow="runFeedbackFlow"
					:workflowVersionId="versionId || workflow.current_draft.id"
					:hasLoadedNodesData="hasLoadedNodesData"
					:isLeftSideDrawerOpen="isLeftSideDrawerOpen"
					:isWideWidget="isWideWidget"
					showMiniMap
				/>
			</div>
		</template>

		<RightSideDrawer :sideBarState="sideBarState" :editNode="editNode" @closeSideBar="closeSideBar">
			<NodeEditor />
		</RightSideDrawer>

		<MoveStepFooter />
		<CreateSubflowFooter />
		<InAppFlowRun
			v-if="showFlowRun"
			:isActive="showFlowRun"
			class="flow-run-container"
			:hashId="sendBuilderFeedbackFlowRun"
			@status-changed="onFeedbackFlowFinished"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

import NodeEditor from './NodeEditor.vue';
import LeftSideDrawer from './drawers/LeftSideDrawer';
import RightSideDrawer from './drawers/RightSideDrawer';
import CreateSubflowFooter from './helpers/CreateSubflowFooter';
import MoveStepFooter from './helpers/MoveStepFooter';
import { Modes } from '@/common.js';
import FlowTree from '@/components/FlowTree/FlowTree.vue';
import { STAGING_FAKE_NOTIFICATION_FLOW_HASH_ID } from '@/components/Onboarding/on-boarding-service';
import WorkflowVersionService from '@/components/WorkflowVersionHistory/workflow-version-service';
import InAppFlowRun from '@/components/common/InAppFlowRun.vue';
import { clearTask } from '@/store/modules/analytics/actions';
import { useIsTaskLogOpen, selectedTaskId, selectedTaskInfo } from '@/store/modules/analytics/getters';
import { getActionsOfApp, getServiceDataBySlug } from '@/store/modules/apps/actions';
import { isBuilderUnderMaintenance } from '@/store/modules/featureFlags/getters';
import { setFocusedElement } from '@/store/modules/flowTree/actions';
import { focusedElement } from '@/store/modules/flowTree/getters';
import { isLeftSideDrawerOpen } from '@/store/modules/leftSideDrawer/getters';
import { closeSideBar } from '@/store/modules/selectedNode/actions';
import { editNode, sideBarState, isFlowTreeDragging, isMiniApp } from '@/store/modules/selectedNode/getters';
import { removeTempBlock } from '@/store/modules/treeBlocks/actions';
import { widgetMetadata } from '@/store/modules/widgetEditor/getters';
import { setIsRouteSelected, setIsSelectingWorkflowRoute } from '@/store/modules/workflowGraphs/actions';
import { getWorkflow, canEditWorkflow } from '@/store/modules/workflows/getters';
import { getVersionOfWorkflow, selectWorkflowVersionId } from '@/store/modules/workflows/modules/versions/actions';
import { useSelectedWorkflowVersionId } from '@/store/modules/workflows/modules/versions/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { PROD_SEND_BUILDER_FEEDBACK_FLOW_HASH_ID } from '@/views/FlowRun/flow-run.const';

export default {
	name: 'Flow',
	props: {
		wfID: { type: Number, required: true },
		hasLoadedNodesData: { type: Boolean },
	},
	components: {
		FlowTree,
		NodeEditor,
		InAppFlowRun,
		RightSideDrawer,
		LeftSideDrawer,
		MoveStepFooter,
		CreateSubflowFooter,
	},
	computed: {
		...mapGetters(['user']),
		...mapGettersShorthand({
			getWorkflow,
			editNode,
			sideBarState,
			isFlowTreeDragging,
			isMiniApp,
			isLeftSideDrawerOpen,
			selectedTaskInfo,
			useSelectedWorkflowVersionId,
			useIsTaskLogOpen,
			selectedTaskId,
			focusedElement,
			widgetMetadata,
			isBuilderUnderMaintenance,
			canEditWorkflow,
		}),
		sendBuilderFeedbackFlowRun() {
			if (process.env.VUE_APP_PLY_ENV === Modes.Production) {
				return PROD_SEND_BUILDER_FEEDBACK_FLOW_HASH_ID;
			} else {
				return STAGING_FAKE_NOTIFICATION_FLOW_HASH_ID;
			}
		},
		hasEditPermissions() {
			return this.canEditWorkflow(this.wfID);
		},
		isTaskLogOpen() {
			return this.useIsTaskLogOpen(this.$route);
		},
		forceFullWidth() {
			return this.sideBarState === 'force-full-width';
		},
		isVersionHistoryOpen() {
			return this.useSelectedWorkflowVersionId;
		},
		isShowingLeftSideDrawer() {
			return this.isLeftSideDrawerOpen || this.isLeftSideInDrawerSpecialEditMode;
		},
		isLeftSideInDrawerSpecialEditMode() {
			return this.isTaskLogOpen || this.isVersionHistoryOpen;
		},
		isWideWidget() {
			if (this.sideBarState === 'widget' && !this.isMiniApp) {
				return this.widgetOptions.is_wide?.active || false;
			} else {
				return false;
			}
		},
		isFileViewerActive() {
			return this.isWidget && this.widgetOptions.file_viewer?.active;
		},
		widgetOptions() {
			return this.widgetMetadata?.options || {};
		},
		workflow() {
			return this.getWorkflow(this.wfID);
		},
		versionId() {
			return this.debugModeWorkflowVersionId || this.useSelectedWorkflowVersionId;
		},
		isDebugging() {
			return !!this.selectedTaskId;
		},
		debugModeWorkflowVersionId() {
			if (this.isDebugging) {
				return Number(this.selectedTaskInfo?.flowVersionId);
			}
			return null;
		},
		_hasLoadedNodesData: {
			get() {
				return this.hasLoadedNodesData;
			},
			set(v) {
				this.$emit('update:hasLoadedNodesData', v);
			},
		},
	},
	methods: {
		...mapActionsShorthand({
			getVersionOfWorkflow,
			selectWorkflowVersionId,
			setFocusedElement,
			clearTask,
			getActionsOfApp,
			removeTempBlock,
			setIsSelectingWorkflowRoute,
			setIsRouteSelected,
			getServiceDataBySlug,
			closeSideBar,
		}),
		onFeedbackFlowFinished(status) {
			if (['canceled', 'error', 'done'].includes(status)) {
				this.showFlowRun = false;
				const url = new URL(window.location.href);
				url.search = ``;
				window.history.pushState('', '', url.toString());
			}
		},
		runFeedbackFlow() {
			const url = new URL(window.location.href);
			url.search = `?email=${this.user.email}`;
			window.history.pushState('', '', url.toString());
			this.showFlowRun = true;
		},
		focusElement(elementToFocus) {
			if (this.focusedElement !== elementToFocus) {
				this.setFocusedElement(elementToFocus);
			}
			if (elementToFocus && this.$refs.flowTree) {
				this.$refs.flowTree.focusOnNode(elementToFocus);
			}
		},

		closeLeftSideDrawer() {
			if (this.isLeftSideInDrawerSpecialEditMode) {
				WorkflowVersionService.closeLeftSideDrawerSpecialRouterView(this.$router);
			}
		},
	},
	beforeDestroy() {
		this.closeLeftSideDrawer();
	},
	data: () => ({
		showFlowRun: false,
		// hasLoadedNodesData: false,
		disableFlowTree: false,
	}),
	watch: {
		focusedElement: {
			handler(newV) {
				this.focusElement(newV);
			},
		},
		isWideWidget: {
			handler() {
				this.focusElement(this.focusedElement);
			},
		},
		editNode: {
			handler(n, o) {
				if ((!o || n.id !== o.id) && !n.value) {
					this.setFocusedElement(null);
				}
			},
			immediate: true,
		},
		sideBarState: {
			handler(newV, oldV) {
				if (newV !== oldV && oldV === 'closed' && this.focusedElement) {
					// Hackish fix for 'widget' Lag
					this.focusElement(this.focusedElement);
				} else {
					if (this.$refs.flowTree) {
						this.$refs.flowTree.focusOnNode(null);
					}
				}
			},
		},
		'$route.params.versionId': {
			async handler(workflowVersionId) {
				const selectedWorkflowVersionId = workflowVersionId ? Number(workflowVersionId) : null;
				this.selectWorkflowVersionId({ selectedWorkflowVersionId });
			},
			immediate: true,
		},
		versionId: {
			async handler(workflowVersionId) {
				if (workflowVersionId && this.isVersionHistoryOpen) {
					try {
						this._hasLoadedNodesData = false;
						await this.getVersionOfWorkflow({ workflowId: this.wfID, workflowVersionId });
					} catch {
						this.$toast.error('Error while getting feature version. please try again later.');
						this.closeLeftSideDrawer();
					} finally {
						this._hasLoadedNodesData = true;
					}
				}
			},
			deep: true,
			immediate: true,
		},
		selectedTaskInfo: {
			async handler(taskLogInfoObject) {
				if (this.isTaskLogOpen && taskLogInfoObject?.flowVersionId) {
					try {
						this.disableFlowTree = false;
						this._hasLoadedNodesData = false;
						await this.getVersionOfWorkflow({ workflowId: this.wfID, workflowVersionId: Number(taskLogInfoObject.flowVersionId) });
					} catch (e) {
						this.disableFlowTree = true;
					} finally {
						this._hasLoadedNodesData = true;
					}
				}
			},
			deep: true,
			immediate: true,
		},
		isShowingLeftSideDrawer: {
			handler(newV) {
				setTimeout(() => {
					this.focusElement(this.focusedElement);
				}, 500);
				if (!newV) {
					this.disableFlowTree = false;
					this.clearTask();
				}
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>

<style scoped lang="scss">
$flow-tree-width-on-wide-mode: 60px;

.flow-max-width-on-wide-mode {
	max-width: $flow-tree-width-on-wide-mode;
}
.flow-view {
	$top: 75px;

	display: flex;
	width: 100%;
	height: 100%;
	background: transparent;

	.pan-zoom-controllers {
		position: absolute;
		top: $top;
	}
}

/* Zoom Section */
.pan-zoom-container {
	position: relative; // for FlowTree <Loader />
	height: 100%;
	width: 100%;
	white-space: nowrap;
	overflow: hidden;
	cursor: grab;
	background-color: var(--page-background);
	&.un-editable-background {
		background-size: auto auto;
		background-color: rgba(250, 252, 255, 1);
		background-image: repeating-linear-gradient(
			-45deg,
			transparent,
			transparent 28px,
			rgba(234, 238, 244, 1) 28px,
			rgba(234, 238, 244, 1) 29px
		);
	}
}
.is-grabbing .pan-zoom-container {
	cursor: grabbing;
}
// ::v-deep .vue-pan-zoom-item,
::v-deep .vue-pan-zoom-scene {
	width: 100%;
	height: 100%;
	border: 0; // Remove focus border
	outline: none; // Remove focus border
}

.back-to-editing-button {
	height: 36px !important;
	font-size: 14px !important;
	border-radius: 8px !important;
	color: #5b5be7 !important;
	font-weight: normal !important;
	text-transform: none !important;
	background: var(--bg-color) !important;
	border: 1px solid #5b5be7 !important;
}

::v-deep {
	.react-flow__handle {
		width: 3px;
		height: 3px;
		min-width: 3px;
		min-height: 3px;
		background: transparent;
	}
}
.feedback-button {
	top: 120px;
	left: 15px;
	position: absolute;
	z-index: $z-index-2;
}
.flow-run-container {
	z-index: 999;
}
</style>
